import { ManageFilter } from '@AMIEWEB/Common';
import { selectFilters, selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { DuplicateFilterAlertModal } from '@AMIEWEB/GlobalSearch/FilterSpecComponents/Actions/DuplicateFilterAlertModal';
import { FilterPreferenceSelector } from '@AMIEWEB/GlobalSearch/FilterSpecComponents/Actions/FilterPreferenceSelector';
import { SaveAsModal } from '@AMIEWEB/GlobalSearch/FilterSpecComponents/Actions/SaveAsModal';
import { SaveModal } from '@AMIEWEB/GlobalSearch/FilterSpecComponents/Actions/SaveModal';
import { Button } from '@mui/material';
import { colors } from 'styles/styleVariables';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { LayoutGridItem, LayoutGrid } from 'app/layout/LayoutGrid';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { RootState } from 'types';
import _ from 'lodash';
import { SessionKey, StorageType, getSessionValue } from 'utils/customHooks/sessionStorage/sessionHelpers';
import {
  checkDuplicateFilters,
  convertArrayToKeyValuePairs,
  convertSelectedFilterValue,
  formatSelectedFilter,
} from './TaskFilters/TaskFilterUtilitis';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { TaskEntityNavigation } from './TaskEntityNavigation';
import { selectTaskEntity } from '../store/Tasks.selectors';
import { TaskEntity } from 'app/models/Tasks/Tasks';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles()({
  button: {
    width: '100%',
    maxWidth: '185px',
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    height: '28px',
    backgroundColor: colors.amnBlue,
    '& .MuiButton-endIcon': {
      right: '10px',
    },
    '& .MuiButton-label': {
      justifyContent: 'flex-start',
    },
    '&:hover': {
      backgroundColor: '#003E68',
    },
  },
});

export const TaskTitleActionWrapper = ({ category, defaultValues, chips }: { category; defaultValues; chips }) => {
  const { classes } = useStyles();
  const filterAttributes = useSelector(selectFilters);
  const gridState = useSelector(selectGridState);
  const taskEntity = useSelector(selectTaskEntity);
  const filterPreferences = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === `GlobalSearchGrid-${category}`);
  });
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isSaveModalOpen, setSaveModalOpen] = useState(false);
  const [isAlertModalOpen, setAlertModalOpen] = useState(false);
  const [isSaveAsModalOpen, setSaveAsModalOpen] = useState(false);
  const [existingFilterSet, setExistingFilterSet] = useState('');
  const [displaySaveButton, setDisplaySaveButton] = useState(false);
  const [displaySaveAsButton, setDisplaySaveAsButton] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(true);
  const [errorText, setErrorText] = useState('');
  const [selectedFilterOption, setSelectedFilterOption] = useState<{ [x: string]: any } | undefined | null>();
  const [isManageFilters, setMangeFilters] = useState(false);
  const dispatch = useDispatch();
  const [newFilterName, setNewFilterName] = useState('');
  const {t} = useTranslation();

  useEffect(() => {
    const foundFilter = filterOptions.find(item => item.name === filterPreferences?.value?.selectedFilter?.name);
    if (foundFilter) {
      setSelectedFilterOption(foundFilter);
    } else {
      setSelectedFilterOption(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPreferences?.value?.selectedFilter, filterOptions]);

  useEffect(() => {
    if (
      isNullOrUndefined(selectedFilterOption) &&
      Object.entries(filterAttributes?.filters || {})?.some(
        item => !isNullOrUndefined(item[1]) || Array.isArray(item[1] && (item[1] as unknown[])?.length > 0),
      )
    ) {
      setDisplaySaveAsButton(true);
      setDisplaySaveButton(false);
    } else {
      setDisplaySaveAsButton(false);
      setDisplaySaveButton(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAttributes?.filters, selectedFilterOption]);

  const isDuplicateFilter = () => {
    let isDuplicate = false;
    filterPreferences?.value?.filterAttributes?.filterSet?.every(item => {
      if (checkDuplicateFilters(Object.fromEntries(item?.value), filterAttributes?.filters)) {
        isDuplicate = true;
        setExistingFilterSet(item.name);
        return false;
      }
      return true;
    });
    return isDuplicate;
  };

  const addNewFilter = () => {
    const tempColumnSort = getSessionValue(SessionKey.tempColumnOrder, StorageType.sessionStorage);
    if (
      newFilterName?.length > 0 &&
      filterOptions?.filter(item => item.name.toLowerCase() === newFilterName.toLowerCase())?.length > 0
    ) {
      setErrorText('Name Already In Use');
      return;
    }

    if (isDuplicateFilter()) {
      setSaveAsModalOpen(false);
      setAlertModalOpen(true);
    } else {
      const filtersTemp = JSON.parse(JSON.stringify(filterAttributes?.filters));
      const gridStateTemp = _.cloneDeep(gridState);

      const newFilterOption = {
        name: newFilterName.toLowerCase(),
        value: Object.entries(filtersTemp),
        ...tempColumnSort,
      };
      const newGridState = {
        ...gridStateTemp,
        filterAttributes: {
          ...gridStateTemp.filterAttributes,
          filterSet: [...(filterPreferences?.value?.filterAttributes?.filterSet ?? []), newFilterOption],
        },
        selectedFilter: {
          name: newFilterOption.name,
          value: newFilterOption.value,
          isDefault: newFilterOption.isDefault,
        },
      };
      dispatch(
        userDevicePreferenceActions.saveUserGridPreferenceAction({
          id: SessionKey[`GlobalSearchGrid-${category}`],
          value: newGridState,
        }),
      );
      dispatch(
        globalActions.setSnackBar({
          message: t('search.filter.addFilterSuccess'),
          severity: 'success',
        }),
      );
      setSaveAsModalOpen(false);
    }
  };

  const handleFilterNameChange = val => {
    setDisableSaveButton(val?.length <= 1 || val?.length >= 31);
    setNewFilterName(val);
    setErrorText('');
  };

  const handleFilterOptionsChange = filterValue => {
    if (!!filterValue) {
      const filterSet = filterPreferences?.value?.filterAttributes?.filterSet.find(item => item.name === filterValue);
      const selectedFilter = {
        name: filterSet.name,
        value: filterSet.value,
        isDefault: filterSet.isDefault,
      };

      const selectedFilterChips = convertSelectedFilterValue(selectedFilter?.value);

      dispatch(
        gridStateActions?.setFilterAttributes({
          filterObject: selectedFilter?.value,
          selectedChipFilters: selectedFilterChips,
        }),
      );
      dispatch(
        userDevicePreferenceActions.saveUserGridPreferenceAction({
          ...filterPreferences,
          value: {
            ...filterPreferences?.value,
            selectedFilter,
          },
        }),
      );
      setDisplaySaveButton(false);
      setDisplaySaveAsButton(false);
    }
  };

  const handleSaveExistingFilter = () => {
    const duplicateFilter = filterPreferences?.value?.filterAttributes?.filterSet.find(item => {
      if (checkDuplicateFilters(Object.fromEntries(item?.value), filterAttributes?.filters)) return item?.value;
    });
    if (duplicateFilter) {
      setAlertModalOpen(true);
      setExistingFilterSet(duplicateFilter.name);
      setSaveAsModalOpen(false);
    } else setSaveModalOpen(true);
  };

  const updateExistingFilter = () => {
    const filterPrefClone = _.cloneDeep(filterPreferences);
    dispatch(
      userDevicePreferenceActions.saveUserGridPreferenceAction({
        id: SessionKey[`GlobalSearchGrid-${category}`],
        value: {
          ...(filterPrefClone?.value ?? {}),
          filterAttributes: {
            ...(filterPrefClone?.value?.filterAttributes ?? {}),
            filterSet: _.cloneDeep(filterPrefClone?.value?.filterAttributes?.filterSet)?.map(item => {
              if (item.name === filterPrefClone?.value?.selectedFilter?.name) {
                item.value = Object.entries(JSON.parse(JSON.stringify(filterAttributes?.filters)));
              }
              return item;
            }),
          },
        },
      }),
    );
    dispatch(
      globalActions.setSnackBar({
        message: t('search.filter.addFilterSuccess'),
        severity: 'success',
      }),
    );
    setSaveModalOpen(false);
    setDisplaySaveButton(false);
    setDisplaySaveAsButton(false);
  };

  useEffect(() => {
    setFilterOptions(
      _.cloneDeep(filterPreferences?.value?.filterAttributes?.filterSet || []).sort((a, b) =>
        a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()),
      ),
    );
    if (
      filterOptions?.length !== 0 &&
      filterOptions?.length > (filterPreferences?.value?.filterAttributes?.filterSet?.length || 0) &&
      !filterPreferences?.value?.filterAttributes?.filterSet?.find(item => item.name === selectedFilterOption?.name)
    ) {
      // Filter option was removed and it was the one that was currently selected
      setSelectedFilterOption(undefined);
      dispatch(
        userDevicePreferenceActions.saveUserGridPreferenceAction({
          id: `GlobalSearchGrid-${category}`,
          value: {
            ...filterPreferences?.value,
            selectedFilter: null,
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPreferences?.value?.filterAttributes?.filterSet]);

  useEffect(() => {
    const selectedPreference = filterPreferences?.value?.filterAttributes?.filterSet?.find(
      item => item.name === selectedFilterOption?.name,
    );
    if (selectedPreference && filterAttributes) {
      const selectedFilterValue = formatSelectedFilter(selectedPreference?.value);
      const selectedFilters = convertArrayToKeyValuePairs(selectedFilterValue);
      if (checkDuplicateFilters(selectedFilters, filterAttributes?.filters)) {
        setDisplaySaveButton(false);
        setDisplaySaveAsButton(false);
      } else {
        setDisplaySaveButton(true);
        setDisplaySaveAsButton(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAttributes, filterPreferences?.value?.selectedFilter?.name, selectedFilterOption]);

  useEffect(() => {
    if (isSaveAsModalOpen === false) setErrorText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveAsModalOpen]);

  return (
    <LayoutGridItem>
      <LayoutGrid alignContent="flex-end">
        <LayoutGridItem>
          <TaskEntityNavigation defaultValues={defaultValues} />
        </LayoutGridItem>
        {taskEntity === TaskEntity.ALL && (
          <React.Fragment>
            <LayoutGridItem>
              <FilterPreferenceSelector
                filterOptions={filterOptions.map(item => item.name)}
                selectedFilterOption={selectedFilterOption?.name || 'SELECT FILTERS'}
                setMangeFilters={setMangeFilters}
                handleFilterOptionsChange={handleFilterOptionsChange}
                size="medium"
              />
            </LayoutGridItem>
            {!!filterOptions?.length && selectedFilterOption?.name !== 'SELECT FILTERS' && displaySaveButton && (
              <LayoutGridItem>
                <Button
                  disableElevation
                  color="primary"
                  className={classes.button}
                  variant="contained"
                  onClick={() => handleSaveExistingFilter()}
                >{`Save`}</Button>
              </LayoutGridItem>
            )}
            {/** Code: displaySaveAsButton OR Display Save As Button to when no filters have been saved yet */}
            {(displaySaveAsButton || !filterOptions?.length) && (
              <LayoutGridItem>
                <Button
                  disableElevation
                  color="primary"
                  onClick={() => setSaveAsModalOpen(true)}
                  className={classes.button}
                  variant="contained"
                >{`Save As`}</Button>
              </LayoutGridItem>
            )}
          </React.Fragment>
        )}
      </LayoutGrid>
      {taskEntity === TaskEntity.ALL && (
        <React.Fragment>
          {/* DIALOGS */}
          <DuplicateFilterAlertModal {...{ isAlertModalOpen, setAlertModalOpen, setMangeFilters, existingFilterSet }} />
          <SaveModal {...{ isSaveModalOpen, setSaveModalOpen, updateExistingFilter }} />
            <SaveAsModal
              {...{
                isSaveAsModalOpen,
                setSaveAsModalOpen,
                disableSaveButton,
                handleFilterNameChange,
                addNewFilter,
                errorText,
                chips,
              }}
            />
          {isManageFilters && (
            <ManageFilter open={isManageFilters} onClose={() => setMangeFilters(false)} category={category} />
          )}
        </React.Fragment>
      )}
    </LayoutGridItem>
  );
};
